/**
 * A stored AI summary, drawn like the live console.
 *
 * The secretary runs the analysis over the day's files before the doctor sees
 * anyone (AiBatchPanel), and the doctor reads each summary when the patient
 * walks in. Those summaries used to be a grey paragraph of raw model text;
 * the live console the independent doctor uses drew the same report as
 * severity rails, with the model's thinking one tap away. This is that, for a
 * report that has already been written: the same findings, the same colours,
 * the tier and model that wrote it, the reasoning it kept
 * (ClinicalNote.meta.reasoning) — and a button to run a fresh analysis live.
 */

import { useMemo, useState } from 'react';
import { Icon } from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { parseReport, stripInline } from './aiStream';
import { shortModel, TONE_LABEL } from './reportDocument';
import { Inline, TONE_ICON, TONE_TEXT } from './findingParts';
import { AiAnalyzeButton } from './AiAnalyzeButton';

const when = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' });
};

/** The report's findings on their severity rails. */
export function FindingRows({ findings }) {
  return (
    <ul className="space-y-2">
      {findings.map((finding, index) => (
        <li key={finding.id} className="ai-finding p-3" data-tone={finding.tone} style={{ animationDelay: `${Math.min(index, 9) * 55}ms` }}>
          <div className="flex items-start gap-2.5">
            <Icon name={TONE_ICON[finding.tone] || 'circle'} size={15} className={`mt-0.5 shrink-0 ${TONE_TEXT[finding.tone] || 'text-slate-400'}`} />
            <div className="min-w-0 flex-1">
              {TONE_LABEL[finding.tone] && (
                <div className={`mb-0.5 text-[9.5px] font-black ${TONE_TEXT[finding.tone] || 'text-slate-500'} opacity-70`}>{TONE_LABEL[finding.tone]}</div>
              )}
              <p className="text-[13.5px] font-bold leading-relaxed text-white">
                <Inline segments={finding.claimSegments} text={finding.claim} />
              </p>
              {finding.cause && (
                <p className="mt-1 text-[12.5px] leading-relaxed text-slate-400">
                  <Inline segments={finding.causeSegments} text={finding.cause} />
                </p>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * @param {object} props
 * @param {object} props.summary     a ClinicalNote of kind ai_summary: { text, createdAt, createdByRole, meta }
 * @param {string} [props.patientName]
 * @param {object} [props.live]      identity for a fresh live analysis: { bookingId, patientId, manualPatientCode, hospitalPatientId }
 * @param {string} [props.title]
 */
export function AiSummaryCard({ summary, patientName = '', live = null, title = 'ملخص الذكاء الاصطناعي' }) {
  const { success, error: toastError } = useNotifications();
  const [thinking, setThinking] = useState(false);
  const report = useMemo(() => parseReport(summary?.text || ''), [summary?.text]);
  if (!summary) return null;

  const meta = summary.meta || {};
  const premium = meta.tier === 'premium';
  const findings = report.followedFormat ? report.findings : [];
  const reasoning = [meta.reasoning, report.followedFormat ? report.aside.join('\n') : ''].filter(Boolean).join('\n').trim();

  const copy = async () => {
    const text = findings.length
      ? findings.map((f) => `${f.marker || '•'} ${stripInline(f.text)}`).join('\n')
      : String(summary.text || '');
    try {
      await navigator.clipboard.writeText(text);
      success('تم نسخ الملخص');
    } catch {
      toastError('تعذر النسخ — المتصفح منع الوصول للحافظة');
    }
  };

  return (
    <section className="ai-card" data-tier={premium ? 'premium' : 'standard'}>
      <header className="flex flex-wrap items-start gap-3">
        <span className="ai-card__orb" data-tier={premium ? 'premium' : 'standard'} aria-hidden="true">
          <Icon name="sparkles" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-black text-white">{title}</h4>
            <span
              className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[10px] font-black ${
                premium ? 'border-amber-400/40 bg-amber-400/15 text-amber-200' : 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200'
              }`}
            >
              <Icon name={premium ? 'crown' : 'zap'} size={11} />
              {premium ? 'الأذكى' : 'الأسرع'}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {summary.createdByRole === 'secretary' ? 'جهّزته السكرتارية' : 'تحليل مسبق'} · {when(summary.createdAt)}
            {meta.model && (
              <span dir="ltr" className="ms-1 text-slate-500">
                · {shortModel(meta.model)}
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button type="button" onClick={copy} className="ai-card__tool" title="نسخ الملخص">
            <Icon name="copy" size={13} /> <span className="hidden sm:inline">نسخ</span>
          </button>
        </div>
      </header>

      <div className="mt-3">
        {findings.length > 0 ? (
          <FindingRows findings={findings} />
        ) : (
          <p className="whitespace-pre-line rounded-xl bg-white/[0.03] p-3 text-sm leading-relaxed text-slate-200">{summary.text}</p>
        )}
      </div>

      {reasoning && (
        <div className="ai-reasoning-shell mt-3 overflow-hidden">
          <button
            type="button"
            onClick={() => setThinking((v) => !v)}
            className="flex w-full items-center gap-2 px-3 py-2 text-start transition-colors hover:bg-white/[0.03]"
            aria-expanded={thinking}
          >
            <Icon name="brain" size={13} className="text-purple-400" />
            <span className="text-[11px] font-bold text-slate-300">مسار تفكير النموذج</span>
            <span className="text-[10px] text-slate-500">{reasoning.length} حرف</span>
            <span className="flex-1" />
            <Icon name={thinking ? 'chevron-up' : 'chevron-down'} size={14} className="text-slate-500" />
          </button>
          {thinking && <div className="custom-scrollbar ai-reasoning max-h-52 overflow-y-auto border-t border-white/[0.07] px-3 py-2">{reasoning}</div>}
        </div>
      )}

      {live && (
        <div className="mt-3">
          <AiAnalyzeButton
            {...live}
            patientName={patientName}
            compact
            label="تحليل مباشر الآن — مع مسار التفكير واختيار النموذج"
          />
        </div>
      )}

      <p className="mt-2 flex items-center gap-1.5 text-[10.5px] text-slate-500">
        <Icon name="shield-check" size={12} /> ملخص مساعد للطبيب وليس تشخيصاً.
      </p>
    </section>
  );
}

export default AiSummaryCard;
